// Sidebar.js - Updated with new design
import React from 'react';
import {
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  Divider,
  Badge
} from '@mui/material';
import {
  LocalShipping,
  Search,
  History,
  Email,
  Numbers
} from '@mui/icons-material';

const Sidebar = ({ 
  drawerWidth, 
  mobileOpen, 
  handleDrawerToggle, 
  activeTab, 
  setActiveTab,
  searchHistory,
  darkMode
}) => {
  const menuItems = [
    { text: 'Search Carrier', icon: <Search />, tab: 'search' },
    { text: 'Search History', icon: <History />, tab: 'history', badge: searchHistory.length }
  ];

  const searchTypes = [
    { text: 'MC Number Search', icon: <Numbers />, type: 'mc', desc: 'Motor Carrier Number' },
    { text: 'DOT Number Search', icon: <LocalShipping />, type: 'dot', desc: 'USDOT Number' },
    { text: 'Email Search', icon: <Email />, type: 'email', desc: 'Registered Email' }
  ];

  const drawer = (
    <Box sx={{ overflow: 'auto', height: '100%' }}>
      {/* Logo Section */}
      <Box sx={{ p: 3, textAlign: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{
          width: 60,
          height: 60,
          background: 'linear-gradient(135deg, #2563eb 0%, #059669 100%)',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px',
          color: 'white',
          fontSize: '24px',
          fontWeight: 'bold'
        }}>
          CFP
        </Box>
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          Carrier Finder Pro
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Professional Search Tool
        </Typography>
      </Box>

      {/* Navigation Menu */}
      <List sx={{ px: 2, py: 3 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
            <ListItemButton
              selected={activeTab === item.tab}
              onClick={() => setActiveTab(item.tab)}
              sx={{
                borderRadius: 2,
                '&.Mui-selected': {
                  backgroundColor: darkMode ? 'rgba(37, 99, 235, 0.16)' : 'rgba(37, 99, 235, 0.08)',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: darkMode ? 'rgba(37, 99, 235, 0.24)' : 'rgba(37, 99, 235, 0.12)',
                  }
                }
              }}
            >
              <ListItemIcon sx={{ 
                color: activeTab === item.tab ? 'primary.main' : 'text.secondary',
                minWidth: 40
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text} 
                primaryTypographyProps={{
                  fontWeight: activeTab === item.tab ? 600 : 400,
                  color: activeTab === item.tab ? 'primary.main' : 'text.primary'
                }}
              />
              {item.badge > 0 && (
                <Chip 
                  label={item.badge} 
                  size="small" 
                  sx={{ 
                    ml: 1,
                    backgroundColor: 'primary.main',
                    color: 'white',
                    fontWeight: 600
                  }} 
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ my: 2 }} />

      {/* Search Types */}
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          SEARCH TYPES
        </Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          {searchTypes.map((item) => (
            <Box
              key={item.type}
              sx={{
                p: 2,
                backgroundColor: darkMode ? 'grey.800' : 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: darkMode ? 'grey.700' : 'grey.100',
                  transform: 'translateX(4px)'
                }
              }}
              onClick={() => setActiveTab('search')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{ 
                  background: 
                    item.type === 'mc' ? 'rgba(37, 99, 235, 0.1)' : 
                    item.type === 'dot' ? 'rgba(5, 150, 105, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                  borderRadius: 1,
                  p: 1,
                  mr: 2,
                  color: 
                    item.type === 'mc' ? 'primary.main' : 
                    item.type === 'dot' ? 'secondary.main' : 'warning.main'
                }}>
                  {item.icon}
                </Box>
                <Box>
                  <Typography variant="body2" fontWeight="600">
                    {item.text}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item.desc}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Quick Stats */}
      <Box sx={{ px: 3, py: 2, mt: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
          QUICK STATS
        </Typography>
        <Box sx={{ display: 'grid', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Total Searches:</Typography>
            <Typography variant="body2" fontWeight="600" color="primary.main">
              {searchHistory.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Today:</Typography>
            <Typography variant="body2" fontWeight="600" color="secondary.main">
              {searchHistory.filter(s => new Date(s.timestamp).toDateString() === new Date().toDateString()).length}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider'
          }
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: drawerWidth,
            backgroundColor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider'
          }
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
};

export default Sidebar;